import { useFormContext } from 'react-hook-form';
import Checkbox from 'components/common/forms/Checkbox';
import TeethCanvas from 'components/common/forms/TeethCanvas';
import FormRadioBtn from 'components/common/forms/FormRadioBtn';
import FormAutoHeightTextarea from 'components/common/forms/FormAutoHeightTextarea';
import styles from 'styles/pages/chart/PlaqueControlRecordChart.module.scss';
import {
  Result,
  ResultLabel,
  PlaqueControlRecordChartFields,
} from 'constants/chart/plaqueControlRecordChart';

type TProps = {
  category: string;
  round1All: boolean;
  setRound1All: (round1All: boolean) => void;
  round2All: boolean;
  setRound2All: (round2All: boolean) => void;
  isRegister: boolean;
  isReadOnly: boolean;
  setCanvas1Ref: (ref: any) => void;
  setCanvas2Ref: (ref: any) => void;
  activeTabIndex: number;
};

const PlaqueControlRecordChart = ({
  category,
  round1All,
  setRound1All,
  round2All,
  setRound2All,
  isRegister,
  isReadOnly,
  setCanvas1Ref,
  setCanvas2Ref,
  activeTabIndex,
}: TProps) => {
  const { register, setValue, watch, getValues } = useFormContext();

  return (
    <>
      <div className={styles.title_wrapper}>
        <span className={styles.rectangle}></span>
        <span className={styles.title}>1차</span>
      </div>
      <Checkbox
        inputId="round1All"
        text="전체 선택"
        checked={round1All}
        readOnly={
          !isRegister ||
          isReadOnly ||
          (category !== 'practice' && activeTabIndex === 2)
        }
        onChange={(e) => {
          setRound1All(e.target.checked);
        }}
      />
      <div className="chart_image_area1">
        <TeethCanvas
          selectAll={round1All}
          className="mt32"
          value={getValues(PlaqueControlRecordChartFields.round1PlaqueIndex)}
          readOnly={
            !isRegister ||
            isReadOnly ||
            (category !== 'practice' && activeTabIndex === 2)
          }
          onChange={(value) => {
            setRound1All(value.length === 128);
            setValue(PlaqueControlRecordChartFields.round1PlaqueIndex, value);
          }}
          setCanvasRef={setCanvas1Ref}
        />
      </div>
      <div className="flex justify_center w100 mt48">
        <dl className={styles.round1_container_left}>
          <dt>Index</dt>
          <dd className="flex flex_column justify_between">
            <div className={styles.calculation}>
              <input
                type="text"
                value={
                  watch(PlaqueControlRecordChartFields.round1PlaqueIndex).length
                }
                readOnly
              />
              <span className="ml14">/</span>
              <span>(</span>
              <input
                type="number"
                value={
                  watch(PlaqueControlRecordChartFields.round1CareIndex) || ''
                }
                readOnly={
                  !isRegister ||
                  isReadOnly ||
                  (category !== 'practice' && activeTabIndex === 2)
                }
                placeholder="검사 치면수"
                onChange={(e) =>
                  setValue(
                    PlaqueControlRecordChartFields.round1CareIndex,
                    Number(e.target.value)
                  )
                }
              />
              <span>X 4</span>
              <span className="ml14">)</span>
              <span>X 100</span>
              <strong>
                =
                <input
                  type="text"
                  value={
                    watch(PlaqueControlRecordChartFields.round1CareIndex)
                      ? (
                          (watch(
                            PlaqueControlRecordChartFields.round1PlaqueIndex
                          ).length /
                            ((watch(
                              PlaqueControlRecordChartFields.round1CareIndex
                            ) || 0) *
                              4)) *
                          100
                        ).toFixed(1)
                      : 0
                  }
                  readOnly
                />
                %
              </strong>
            </div>
            <ul className={styles.description}>
              <li>
                <span>※</span>
                (착색이 있는 치면수) / (검사 치면수*4) *100
              </li>
              <li>
                <span>※</span>
                10% 이하 수준으로 관리 추천
                <br />
                (점수가 낮을수록 구강 관리가 잘 된다는 의미)
              </li>
            </ul>
          </dd>
        </dl>
        <dl className={styles.round1_container_center}>
          <dt>다음 내원 시 목표 index</dt>
          <dd className={styles.textarea_wrapper}>
            <input
              {...register(PlaqueControlRecordChartFields.nextGoal)}
              type="number"
              readOnly={
                !isRegister ||
                isReadOnly ||
                (category !== 'practice' && activeTabIndex === 2)
              }
              placeholder="다음 내원 시 목표 index를 입력해 주세요."
            />
          </dd>
        </dl>
        <dl className={styles.round1_container_right}>
          <dt>치면세균막 관리 방법</dt>
          <dd className={styles.textarea_wrapper}>
            <FormAutoHeightTextarea
              name={PlaqueControlRecordChartFields.plaqueManagementMethod}
              readOnly={
                !isRegister ||
                isReadOnly ||
                (category !== 'practice' && activeTabIndex === 2)
              }
              placeholder="치면세균막 관리 방법을 입력해 주세요."
              textareaStyle={{
                width: '100%',
                minHeight: '246px',
                padding: '12px 16px',
                border: 'none',
                marginLeft: '0',
              }}
            />
          </dd>
        </dl>
      </div>

      <div className={styles.title_wrapper}>
        <span className={styles.rectangle}></span>
        <span className={styles.title}>2차</span>
      </div>
      <Checkbox
        inputId="round2All"
        text="전체 선택"
        checked={round2All}
        readOnly={
          !isRegister ||
          isReadOnly ||
          (category !== 'practice' && activeTabIndex === 0)
        }
        onChange={(e) => {
          setRound2All(e.target.checked);
        }}
      />
      <div className="chart_image_area2">
        <TeethCanvas
          selectAll={round2All}
          className="mt32"
          value={getValues(PlaqueControlRecordChartFields.round2PlaqueIndex)}
          readOnly={
            !isRegister ||
            isReadOnly ||
            (category !== 'practice' && activeTabIndex === 0)
          }
          onChange={(value) => {
            setRound2All(value.length === 128);
            setValue(PlaqueControlRecordChartFields.round2PlaqueIndex, value);
          }}
          setCanvasRef={setCanvas2Ref}
        />
      </div>
      <div className="flex justify_center w100 mt48">
        <dl className={styles.round2_container_left}>
          <dt>Index</dt>
          <dd className="flex justify_center items_center">
            <div className={styles.calculation}>
              <input
                type="text"
                value={
                  watch(PlaqueControlRecordChartFields.round2PlaqueIndex).length
                }
                readOnly
              />
              <span className="ml14">/</span>
              <span>(</span>
              <input
                type="number"
                value={
                  watch(PlaqueControlRecordChartFields.round2CareIndex) || ''
                }
                readOnly={
                  !isRegister ||
                  isReadOnly ||
                  (category !== 'practice' && activeTabIndex === 0)
                }
                placeholder="검사 치면수"
                onChange={(e) =>
                  setValue(
                    PlaqueControlRecordChartFields.round2CareIndex,
                    Number(e.target.value)
                  )
                }
              />
              <span>X 4</span>
              <span className="ml14">)</span>
              <span>X 100</span>
              <strong>
                =
                <input
                  type="text"
                  value={
                    watch(PlaqueControlRecordChartFields.round2CareIndex)
                      ? (
                          (watch(
                            PlaqueControlRecordChartFields.round2PlaqueIndex
                          ).length /
                            ((watch(
                              PlaqueControlRecordChartFields.round2CareIndex
                            ) || 0) *
                              4)) *
                          100
                        ).toFixed(1)
                      : 0
                  }
                  readOnly
                />
                %
              </strong>
            </div>
          </dd>
        </dl>
        <dl className={styles.round2_container_right}>
          <dt>목표</dt>
          <dd className="flex justify_center items_center">
            <FormRadioBtn
              name={PlaqueControlRecordChartFields.result}
              labelText={ResultLabel[Result.success]}
              value={Result.success}
              readOnly={
                !isRegister ||
                isReadOnly ||
                (category !== 'practice' && activeTabIndex === 0)
              }
              labelStyle={{ marginRight: '24px' }}
            />
            <FormRadioBtn
              name={PlaqueControlRecordChartFields.result}
              labelText={ResultLabel[Result.failure]}
              value={Result.failure}
              readOnly={
                !isRegister ||
                isReadOnly ||
                (category !== 'practice' && activeTabIndex === 0)
              }
            />
          </dd>
        </dl>
      </div>
    </>
  );
};

export default PlaqueControlRecordChart;
